import { Link, useRouterState } from "@tanstack/react-router";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { BUSINESS, services, cities, serviceLink, cityLink } from "@/lib/site";
import { getLenis } from "@/lib/lenis";
import { Logo } from "./Logo";
import { QuoteButton } from "./Buttons";
import { useGoToQuote } from "@/lib/quoteNav";

const NAV: { label: string; to: string; children?: { label: string; to: string }[] }[] = [
  { label: "Home", to: "/" },
  {
    label: "Services",
    to: "/services",
    children: services.map((s) => ({ label: s.name, to: `/services/${s.slug}` })),
  },
  {
    label: "Service Areas",
    to: "/service-areas",
    children: cities.map((c) => ({ label: `${c.name}, AL`, to: `/service-areas/${c.slug}` })),
  },
  { label: "Gallery", to: "/gallery" },
  { label: "About", to: "/about" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hover, setHover] = useState<string | null>(null);
  // Mobile accordion state — both start collapsed.
  const [servicesOpen, setServicesOpen] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);
  const goQuote = useGoToQuote();

  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const firstRun = useRef(true);

  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the menu whenever the route changes.
  useEffect(() => {
    if (open) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Close when resized up to desktop.
  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      if (window.matchMedia("(min-width: 1024px)").matches) setOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Pause Lenis while the menu is open so the page behind doesn't scroll;
  // resume when it closes. Always restarted if the header unmounts.
  useEffect(() => {
    const lenis = getLenis();
    if (!lenis) return;
    if (open) lenis.stop();
    else lenis.start();
  }, [open]);

  useEffect(() => {
    return () => {
      getLenis()?.start();
    };
  }, []);

  // Reliable scroll lock (works on iOS) — lock both html and body, restore on
  // close. We do not touch the scroll position.
  useEffect(() => {
    if (open) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    }
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, [open]);

  // Focus management: move focus to the close button on open, back to the
  // hamburger on close (but not on the very first render).
  useEffect(() => {
    if (open) {
      closeRef.current?.focus();
    } else if (!firstRun.current) {
      hamburgerRef.current?.focus();
    }
    firstRun.current = false;
  }, [open]);

  const close = () => setOpen(false);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 h-[72px] transition-all duration-300 md:h-[84px]",
        scrolled
          ? "border-b border-primary/20 bg-background/80 backdrop-blur-md"
          : "border-b border-transparent bg-transparent",
      )}
      style={{ boxShadow: scrolled ? "0 8px 30px -20px rgba(0,0,0,0.3)" : "none" }}
    >
      <div className="mx-auto flex h-full max-w-6xl items-center justify-between gap-4 px-4 md:px-6">
        <Logo scrolled={scrolled} />

        <nav
          className="hidden items-center gap-1 lg:flex"
          aria-label="Primary"
          onMouseLeave={() => setHover(null)}
        >
          {NAV.map((n) => (
            <div
              key={n.to}
              className="relative"
              onMouseEnter={() => setHover(n.children ? n.to : null)}
            >
              <Link
                to={n.to}
                className="nav-underline rounded-md px-3 py-2 text-sm font-medium text-ink/80 transition-colors hover:text-primary"
                activeProps={{ "data-active": "true" }}
              >
                {n.label}
                {n.children && (
                  <ChevronDown
                    className={cn(
                      "ml-1 inline h-3 w-3 transition-transform",
                      hover === n.to && "rotate-180",
                    )}
                  />
                )}
              </Link>
              {n.children && hover === n.to && (
                <div className="absolute left-0 top-full z-50 w-60 pt-3">
                  <div className="rounded-2xl border border-border bg-popover p-2 shadow-xl">
                    {n.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        onClick={() => setHover(null)}
                        className="block rounded-lg px-3 py-2 text-sm text-ink/80 transition-colors hover:bg-secondary hover:text-primary"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={BUSINESS.phoneHref}
            className="hidden items-center gap-1.5 text-sm font-semibold text-ink md:inline-flex"
          >
            <Phone className="h-4 w-4 text-primary" />
            {BUSINESS.phoneDisplay}
          </a>
          <QuoteButton onClick={goQuote} className="hidden sm:inline-flex">
            Get a Quote
          </QuoteButton>
          <button
            type="button"
            ref={hamburgerRef}
            aria-label="Open menu"
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-ink hover:bg-secondary lg:hidden"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
            onClick={close}
            aria-hidden
          />
          <div
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            className="absolute right-0 top-0 flex h-full w-[88%] max-w-sm flex-col bg-background shadow-xl"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <Logo size="sm" />
              <button
                type="button"
                ref={closeRef}
                aria-label="Close menu"
                onClick={close}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md text-ink hover:bg-secondary"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 overscroll-contain" data-lenis-prevent>
              <nav className="flex flex-col" aria-label="Mobile">
                <MobileLink to="/" label="Home" onClick={close} />
                <MobileAccordion
                  label="Services"
                  to="/services"
                  panelId="mobile-services"
                  open={servicesOpen}
                  onToggle={() => setServicesOpen((v) => !v)}
                  onNavigate={close}
                >
                  {services.map((s) => (
                    <Link
                      key={s.slug}
                      {...serviceLink(s.slug)}
                      onClick={close}
                      className="rounded-md px-3 py-2 text-sm text-ink/80 hover:text-primary"
                    >
                      {s.name}
                    </Link>
                  ))}
                </MobileAccordion>
                <MobileAccordion
                  label="Service Areas"
                  to="/service-areas"
                  panelId="mobile-areas"
                  open={areasOpen}
                  onToggle={() => setAreasOpen((v) => !v)}
                  onNavigate={close}
                >
                  {cities.map((c) => (
                    <Link
                      key={c.slug}
                      {...cityLink(c.slug)}
                      onClick={close}
                      className="rounded-md px-3 py-2 text-sm text-ink/80 hover:text-primary"
                    >
                      {c.name}, {c.state}
                    </Link>
                  ))}
                </MobileAccordion>
                <MobileLink to="/gallery" label="Gallery" onClick={close} />
                <MobileLink to="/about" label="About" onClick={close} />
                <MobileLink to="/faq" label="FAQ" onClick={close} />
                <MobileLink to="/contact" label="Contact" onClick={close} />
              </nav>
            </div>
            <div className="space-y-3 border-t border-border p-4">
              <a
                href={BUSINESS.phoneHref}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary bg-transparent px-4 py-3 text-sm font-semibold text-primary"
              >
                <Phone className="h-4 w-4" /> Call {BUSINESS.phoneDisplay}
              </a>
              <QuoteButton onClick={goQuote} className="w-full">
                Get a Quote
              </QuoteButton>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, label, onClick }: { to: string; label: string; onClick: () => void }) {
  return (
    <Link
      to={to as "/"}
      onClick={onClick}
      className="rounded-md px-3 py-2.5 text-base font-medium text-ink/90 hover:bg-secondary hover:text-primary"
    >
      {label}
    </Link>
  );
}

function MobileAccordion({
  label,
  to,
  panelId,
  open,
  onToggle,
  onNavigate,
  children,
}: {
  label: string;
  to: string;
  panelId: string;
  open: boolean;
  onToggle: () => void;
  onNavigate: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-1">
      <div className="flex items-center justify-between gap-2 rounded-md px-3 py-2.5">
        <Link
          to={to as "/"}
          onClick={onNavigate}
          className="flex-1 text-base font-medium text-ink/90 hover:text-primary"
        >
          {label}
        </Link>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={`${open ? "Collapse" : "Expand"} ${label}`}
          onClick={onToggle}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink/70 hover:bg-secondary hover:text-primary"
        >
          <ChevronDown
            className={cn(
              "h-5 w-5 transition-transform motion-reduce:transition-none",
              open && "rotate-180",
            )}
          />
        </button>
      </div>
      <div
        id={panelId}
        className="grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div className="mt-1 flex flex-col gap-0.5 border-l border-primary/40 pl-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
